import {connect} from 'react-redux';
import {type ActionCreatorsMapObject, bindActionCreators, type Dispatch} from 'redux';

import {type GlobalState} from 'types/store/index';

import {type PostDraft} from 'types/store/rhs';

import {type ModalData} from 'types/actions';

import {type ActionFunc, type ActionResult, type DispatchFunc} from 'mattermost-redux/types/actions';

import {getConfig, getLicense} from 'mattermost-redux/selectors/entities/general';
import {haveIChannelPermission} from 'mattermost-redux/selectors/entities/roles';
import {getBool, isCustomGroupsEnabled} from 'mattermost-redux/selectors/entities/preferences';
import {
    getAllChannelStats,
} from 'mattermost-redux/selectors/entities/channels';
import {makeGetMessageInHistoryItem} from 'mattermost-redux/selectors/entities/posts';
import {resetCreatePostRequest, resetHistoryIndex} from 'mattermost-redux/actions/posts';
import {
    getChannelTimezones,
} from 'mattermost-redux/actions/channels';
import {Permissions, Preferences, Posts} from 'mattermost-redux/constants';
import {getAssociatedGroupsForReferenceByMention} from 'mattermost-redux/selectors/entities/groups';

import {connectionErrorCount} from 'selectors/views/system';

import {Constants} from 'utils/constants';
import {getCurrentLocale} from 'selectors/i18n';

import {makeOnMoveHistoryIndex, makeOnSubmit, makeOnEditLatestPost} from 'actions/views/create_comment';
import {emitShortcutReactToLastPostFrom, unsetEditingPost} from 'actions/post_actions';
import {getIsRhsExpanded, getSelectedPostFocussedAt} from 'selectors/rhs';
import {showPreviewOnCreateComment} from 'selectors/views/textbox';
import {setShowPreviewOnCreateComment} from 'actions/views/textbox';
import {openModal} from 'actions/views/modals';

import {getCurrentTeam, getTeam} from 'mattermost-redux/selectors/entities/teams';

import {setDraftAction} from 'features/drafts';

import {getEditingPost} from 'selectors/posts';

import {WithCommentDraft} from './with_comment_draft';

type OwnProps = {
    rootId: string;
    channelId: string;
    latestPostId: string;
};

function makeMapStateToProps() {
    const getMessageInHistoryItem = makeGetMessageInHistoryItem(Posts.MESSAGE_TYPES.COMMENT as 'comment');

    return (state: GlobalState, ownProps: OwnProps) => {
        const err = state.requests.posts.createPost.error || {};

        const channelMembersCount = getAllChannelStats(state)[ownProps.channelId] ? getAllChannelStats(state)[ownProps.channelId].member_count : 1;
        const messageInHistory = getMessageInHistoryItem(state);

        const channel = state.entities.channels.channels[ownProps.channelId] || {};

        const config = getConfig(state);
        const license = getLicense(state);
        const enableConfirmNotificationsToChannel = config.EnableConfirmNotificationsToChannel === 'true';
        const enableEmojiPicker = config.EnableEmojiPicker === 'true';
        const badConnection = connectionErrorCount(state) > 1;
        const isTimezoneEnabled = config.ExperimentalTimezone === 'true';

        const canPost = haveIChannelPermission(state, channel.team_id, channel.id, Permissions.CREATE_POST_IN_THREAD);

        const useChannelMentions = haveIChannelPermission(
            state,
            channel.team_id,
            channel.id,
            Permissions.USE_CHANNEL_MENTIONS,
        );

        const isLDAPEnabled = license?.IsLicensed === 'true' && license?.LDAPGroups === 'true';
        const useGroupMentions = haveIChannelPermission(state, channel.team_id, channel.id, Permissions.USE_GROUP_MENTIONS);
        const useCustomGroupMentions = useGroupMentions && isCustomGroupsEnabled(state);
        const useLDAPGroupMentions = useGroupMentions && isLDAPEnabled;
        const groupsWithAllowReference =
            useLDAPGroupMentions || useCustomGroupMentions ? getAssociatedGroupsForReferenceByMention(state, channel.team_id, channel.id) : null;

        const currentTeam = getCurrentTeam(state);
        const team = getTeam(state, channel?.team_id) || currentTeam;
        const editingPost = getEditingPost(state);

        return {
            messageInHistory,
            channelType: channel.type,
            channelMembersCount,
            codeBlockOnCtrlEnter: getBool(state, Preferences.CATEGORY_ADVANCED_SETTINGS, 'code_block_ctrl_enter', true),
            ctrlSend: getBool(state, Preferences.CATEGORY_ADVANCED_SETTINGS, 'send_on_ctrl_enter'),
            createPostErrorId: err.server_error_id,
            enableConfirmNotificationsToChannel,
            enableEmojiPicker,
            locale: getCurrentLocale(state),
            maxPostSize: parseInt(config.MaxPostSize || '', 10) || Constants.DEFAULT_CHARACTER_LIMIT,
            rhsExpanded: getIsRhsExpanded(state),
            badConnection,
            isTimezoneEnabled,
            selectedPostFocussedAt: getSelectedPostFocussedAt(state),
            canPost,
            useChannelMentions,
            shouldShowPreview: showPreviewOnCreateComment(state),
            groupsWithAllowReference,
            useLDAPGroupMentions,
            useCustomGroupMentions,
            teamName: team.name,
            isEditing: editingPost.show && editingPost.isRHS,
        };
    };
}

type Actions = {
    setDraft: typeof setDraftAction;
    onSubmit: (draft: PostDraft, options: {ignoreSlash: boolean}) => void;
    onResetHistoryIndex: () => void;
    onMoveHistoryIndexBack: () => void;
    onMoveHistoryIndexForward: () => void;
    onEditLatestPost: () => ActionResult;
    resetCreatePostRequest: () => void;
    getChannelTimezones: (channelId: string) => Promise<ActionResult>;
    emitShortcutReactToLastPostFrom: (location: string) => void;
    setShowPreview: (showPreview: boolean) => void;
    openModal: <P>(modalData: ModalData<P>) => void;
    unsetEditingPost: () => void;
};

function makeMapDispatchToProps() {
    let onSubmit: (
        draft: PostDraft,
        options: {ignoreSlash: boolean},
    ) => (dispatch: DispatchFunc, getState: () => GlobalState) => Promise<ActionResult | ActionResult[]> | ActionResult;
    let onMoveHistoryIndexBack: () => (
        dispatch: DispatchFunc,
        getState: () => GlobalState,
    ) => Promise<ActionResult | ActionResult[]> | ActionResult;
    let onMoveHistoryIndexForward: () => (
        dispatch: DispatchFunc,
        getState: () => GlobalState,
    ) => Promise<ActionResult | ActionResult[]> | ActionResult;
    let onEditLatestPost: () => ActionFunc;

    function onResetHistoryIndex() {
        return resetHistoryIndex(Posts.MESSAGE_TYPES.COMMENT);
    }

    let rootId: string;
    let channelId: string;
    let latestPostId: string;

    return (dispatch: Dispatch, ownProps: OwnProps) => {
        if (rootId !== ownProps.rootId) {
            onMoveHistoryIndexBack = makeOnMoveHistoryIndex(ownProps.channelId, ownProps.rootId, -1);
            onMoveHistoryIndexForward = makeOnMoveHistoryIndex(ownProps.channelId, ownProps.rootId, 1);
        }

        if (rootId !== ownProps.rootId) {
            onEditLatestPost = makeOnEditLatestPost(ownProps.rootId);
        }

        if (rootId !== ownProps.rootId || channelId !== ownProps.channelId || latestPostId !== ownProps.latestPostId) {
            onSubmit = makeOnSubmit(ownProps.channelId, ownProps.rootId, ownProps.latestPostId);
        }

        rootId = ownProps.rootId;
        channelId = ownProps.channelId;
        latestPostId = ownProps.latestPostId;

        return bindActionCreators<ActionCreatorsMapObject<any>, Actions>(
            {

                // clearCommentDraftUploads,
                setDraft: setDraftAction,
                onSubmit,
                onResetHistoryIndex,
                onMoveHistoryIndexBack,
                onMoveHistoryIndexForward,
                onEditLatestPost,
                resetCreatePostRequest,
                getChannelTimezones,
                emitShortcutReactToLastPostFrom,
                setShowPreview: setShowPreviewOnCreateComment,
                openModal,
                unsetEditingPost,
            },
            dispatch,
        );
    };
}

export default connect(makeMapStateToProps, makeMapDispatchToProps, null, {forwardRef: true})(WithCommentDraft);
